// composables/useFieldChangeTracker.js
import { ref, computed } from "vue";

export const useFieldChangeTracker = (initialValues) => {
  const originalValues = ref({ ...initialValues });
  const changedFields = ref(new Set());

  // Track changes as they happen
  const trackChange = (fieldName, newValue) => {
    const originalValue = originalValues.value[fieldName];
    if (newValue !== originalValue) {
      changedFields.value.add(fieldName);
    } else {
      changedFields.value.delete(fieldName);
    }
  };

  const REOPEN_ELIGIBLE_FIELDS_NOT_SUPPORTED = [
    "bin",
    "processorControlNumber",
    "grpNumber",
    "nationalDrugCode",
    "medication",
  ];

  const REOPEN_ELIGIBLE_FIELDS_RESPONSE_RECEIVED = [
    // Patient information
    "patientPrefix",
    "patientFirstName",
    "patientMiddleName",
    "patientLastName",
    "patientSuffix",
    "patientDob",
    "patientGender",
    "patientAddress1",
    "patientCity",
    "patientState",
    "patientZip",
    "patientMemberId",
    "patientPhone",
    // Provider information
    "providerFirstName",
    "providerLastName",
    "providerNpiNumber",
    "providerAddress1",
    "providerCity",
    "providerState",
    "providerZip",
    "providerContactPhone",
    "providerContactFax",
    // Insurance information
    "insuranceProvider",
    "insurancePlanType",
    "benefitType",
    "bin",
    "processorControlNumber",
    "grpNumber",
    // Medication information
    "medication",
    "strength",
    "dailyDosage",
    "nationalDrugCode",
    "packageSizeUnit",
    "frequency",
    "routeAdministration",
    "quantityPer30Days",
    "expectedLengthOfTherapy",
    "treatmentStartDate",
    // Diagnosis information
    "primaryDiagnosisIcd10Code",
    "primaryDiagnosisName",
  ];

  // Check if user has made enough significant changes for re-opening
  const isReopenEligible = computed(() => {
    if (originalValues.value.submissionMethod === "CoverMyMeds") {
      if (originalValues.value.status === "Response received") {
        const significantChanges = REOPEN_ELIGIBLE_FIELDS_RESPONSE_RECEIVED.filter((field) =>
          changedFields.value.has(field)
        );
        return significantChanges.length >= 1;
      } else if (originalValues.value.status === "Payor/Medication not supported") {
        const significantChanges = REOPEN_ELIGIBLE_FIELDS_NOT_SUPPORTED.filter((field) =>
          changedFields.value.has(field)
        );
        return significantChanges.length >= 1;
      } else {
        return false;
      }
    } else {
      return false;
    }
  });

  // Check if changes require creating a new submission (not just resubmitting)
  const requiresNewSubmission = computed(() => {
    if (originalValues.value.submissionMethod === "CoverMyMeds") {
      const notSupportedChanges = REOPEN_ELIGIBLE_FIELDS_NOT_SUPPORTED.filter((field) =>
        changedFields.value.has(field)
      );
      return notSupportedChanges.length >= 1;
    }
    return false;
  });

  return {
    trackChange,
    isReopenEligible,
    requiresNewSubmission,
    hasChanges: computed(() => changedFields.value.size > 0),
    changedFields: computed(() => Array.from(changedFields.value)),
  };
};
